import type { ReactNode } from 'react'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { cn } from '@/lib/utils'

export const DETAIL_FIELD_LABEL_CLASS =
  'font-body text-text-subtle block text-[11px] font-medium'
export const DETAIL_FIELD_VALUE_CLASS =
  'font-body text-text-body text-[14px] font-normal leading-[1.65]'

export const DETAIL_SECTION_TITLE_CLASS =
  'text-[10px] font-medium tracking-wider text-text-subtle uppercase'

export function DetailPanelLayout({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex h-[calc(var(--app-vh)_-_50px)] max-h-[calc(var(--app-vh)_-_50px)] min-h-0 w-full flex-1 overflow-hidden',
        className
      )}
    >
      {children}
    </div>
  )
}

export function DetailPanelMain({
  children,
  className,
  viewClassName,
  contentClassName,
}: {
  children: ReactNode
  className?: string
  viewClassName?: string
  contentClassName?: string
}) {
  return (
    <ScrollArea
      className={cn('h-full min-h-0 min-w-0 flex-1 bg-card', className)}
      viewClassName={cn('[&>div]:!block mx-auto px-8 pt-8 pb-8', viewClassName)}
    >
      <div className={cn('mx-auto w-full h-full max-w-5xl pb-8', contentClassName)}>
        {children}
      </div>
    </ScrollArea>
  )
}

export function DetailPanelAside({
  children,
  className,
  contentClassName,
}: {
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  return (
    <aside
      className={cn(
        'border-border bg-surface-subtle hidden h-full w-[320px] shrink-0 overflow-hidden border-l lg:block',
        className
      )}
    >
      <ScrollArea className='h-full' viewClassName='[&>div]:!block [&>div]:h-full'>
        <div className={cn('space-y-6 p-4', contentClassName)}>{children}</div>
      </ScrollArea>
    </aside>
  )
}

export function DetailPanelSkeleton() {
  return (
    <DetailPanelLayout>
      <DetailPanelMain>
        <header>
          <Skeleton className='h-3 w-14 rounded-sm' />
          <div className='mt-3 flex items-center gap-3'>
            <Skeleton className='h-7 w-[420px] max-w-[70vw] rounded-sm' />
            <Skeleton className='h-6 w-14 rounded-sm' />
          </div>
        </header>

        <div className='mt-6 grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3 lg:grid-cols-6'>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className='space-y-2'>
              <Skeleton className='h-3 w-20 rounded-sm' />
              <Skeleton className='h-4 w-28 max-w-full rounded-sm' />
            </div>
          ))}
        </div>

        <Skeleton className='mt-6 h-[106px] w-full rounded-sm' />

        <div className='mt-7'>
          <div className='border-border flex h-9 items-center gap-1 border-b'>
            {[56, 72, 48, 56].map((width, index) => (
              <Skeleton
                key={index}
                className='mx-3 h-4 rounded-sm'
                style={{ width }}
              />
            ))}
          </div>

          <div className='mt-6 space-y-6'>
            <section className='space-y-3'>
              <Skeleton className='h-3 w-28 rounded-sm' />
              <Skeleton className='h-4 w-[520px] max-w-full rounded-sm' />
            </section>

            <section className='space-y-3'>
              <Skeleton className='h-3 w-28 rounded-sm' />
              <Skeleton className='h-4 w-[640px] max-w-full rounded-sm' />
            </section>

            <section className='space-y-5'>
              <Skeleton className='h-3 w-32 rounded-sm' />
              <div className='grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-3'>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className='space-y-2'>
                    <Skeleton className='h-3 w-28 rounded-sm' />
                    <Skeleton className='h-4 w-24 rounded-sm' />
                  </div>
                ))}
              </div>
            </section>

            <section className='space-y-3'>
              <Skeleton className='h-3 w-16 rounded-sm' />
              <div className='border-border space-y-3 rounded-sm border p-4'>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className='flex items-center gap-3'>
                    <Skeleton className='size-4 shrink-0 rounded-full' />
                    <Skeleton className='h-3 w-72 max-w-full rounded-sm' />
                    <Skeleton className='ml-auto h-3 w-12 rounded-sm' />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </DetailPanelMain>
    </DetailPanelLayout>
  )
}

export function DetailAsideSection({
  title,
  children,
  className,
}: {
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <section className={className}>
      <h4 className='text-muted-foreground text-xs font-semibold tracking-wide uppercase'>
        {title}
      </h4>
      <div className='text-text-body mt-3 flex min-h-5 items-center gap-1.5 text-[13px]'>
        {children}
      </div>
    </section>
  )
}

export function DetailPanelTitle({
  code,
  title,
  children,
  className,
}: {
  code?: ReactNode
  title: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <header className={className}>
      {code && (
        <div className='mb-3 flex flex-wrap items-center gap-2'>
          <span className='font-heading text-[13px] font-semibold text-cobalt-strong'>
            {code}
          </span>
        </div>
      )}
      <h1 className='font-heading text-text-secondary mt-0 !text-[24px] leading-[1.3] font-bold'>
        {title}
      </h1>
      {children}
    </header>
  )
}

export function DetailStatusGrid({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid max-w-3xl grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2',
        className
      )}
    >
      {children}
    </div>
  )
}

export function DetailStatusItem({
  label,
  children,
  className,
}: {
  label: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <span className={DETAIL_FIELD_LABEL_CLASS}>
        {label}
      </span>
      {children}
    </div>
  )
}

export function DetailSection({
  title,
  children,
  className,
  headerActions,
  actionPosition,
}: {
  title: string
  children: ReactNode
  className?: string
  headerActions?: ReactNode
  actionPosition?: 'left' | 'right'
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <div
        className={cn(
          'flex items-center',
          actionPosition === 'right' && 'justify-between'
        )}
      >
        <h2 className={DETAIL_SECTION_TITLE_CLASS}>{title}</h2>
        {headerActions && <div className=''>{headerActions}</div>}
      </div>
      {children}
    </section>
  )
}

export function DetailFieldLabel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'font-body text-text-subtle block text-[11px] font-semibold',
        className
      )}
    >
      {children}
    </span>
  )
}
